import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useIdentity, personLabel } from '../context/IdentityContext';
import {
  EmailCampaign,
  EmailCampaignStatus,
  CampaignRecipient,
  CampaignRecipientStatus,
  ContactEngagement,
  getCampaignConfig,
  listCampaigns,
  listCampaignRecipients,
  createCampaign,
  approveCampaign,
  recordPreviewGo,
  sendCampaign,
  pauseCampaign,
  cancelCampaign,
  deleteCampaign,
  archiveCampaign,
  unarchiveCampaign,
  getEngagementReport,
} from '../services/api';

// ============================================================================
// Email Campaigns: bulk personalized sends. AI stages a campaign (name,
// subject, template body, recipient list or CSV); a human approves before
// anything goes out; approving unlocks Send, which drips the campaign out at
// a configurable interval. This page lists campaigns with live progress,
// lets a reviewer drill into one campaign's recipients, and stages new ones.
// ============================================================================

const fmtTime = (iso: string | null): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const errMsg = (err: any, fallback: string): string => err?.response?.data?.detail || fallback;

const STATUS_META: Record<EmailCampaignStatus, { label: string; key: 'success' | 'warning' | 'error' | 'primary' | 'info' | 'textSecondary' }> = {
  draft: { label: 'Draft', key: 'primary' },
  awaiting_joshua_preview: { label: "Awaiting Joshua's preview", key: 'warning' },
  approved: { label: 'Approved', key: 'info' },
  sending: { label: 'Sending', key: 'warning' },
  paused: { label: 'Paused', key: 'warning' },
  sent: { label: 'Sent', key: 'success' },
  failed: { label: 'Failed', key: 'error' },
  cancelled: { label: 'Cancelled', key: 'textSecondary' },
};
// A status this map doesn't know about must never take down the whole page (it did: a backend
// enum value shipped without a matching frontend entry crashed every campaign card on mount,
// "Cannot read properties of undefined (reading 'key')"). Fall back to a plain, honest label.
const UNKNOWN_STATUS_META = { label: 'Unknown status', key: 'textSecondary' as const };

const RECIPIENT_STATUS_META: Record<CampaignRecipientStatus, { label: string; key: 'success' | 'warning' | 'error' | 'primary' | 'textSecondary' }> = {
  pending: { label: 'Pending', key: 'primary' },
  sent: { label: 'Sent', key: 'success' },
  failed: { label: 'Failed', key: 'error' },
  skipped: { label: 'Skipped', key: 'textSecondary' },
};

// Per-recipient engagement badges: which timestamp field lights up which pill.
const RECIPIENT_ENGAGEMENT_META: {
  field: 'delivered_at' | 'opened_at' | 'clicked_at' | 'bounced_at' | 'complained_at' | 'unsubscribed_at';
  label: string;
  key: 'success' | 'warning' | 'error' | 'primary' | 'info' | 'textSecondary';
}[] = [
  { field: 'delivered_at', label: 'Delivered', key: 'success' },
  { field: 'opened_at', label: 'Opened', key: 'info' },
  { field: 'clicked_at', label: 'Clicked', key: 'primary' },
  { field: 'bounced_at', label: 'Bounced', key: 'error' },
  { field: 'complained_at', label: 'Spam', key: 'warning' },
  { field: 'unsubscribed_at', label: 'Unsub', key: 'textSecondary' },
];

// Campaign-level engagement stat chips: count field + rate label + color.
const ENGAGEMENT_STAT_META: {
  field: 'delivered' | 'opened' | 'clicked' | 'bounced' | 'complained' | 'unsubscribed';
  label: string;
  key: 'success' | 'warning' | 'error' | 'primary' | 'info' | 'textSecondary';
}[] = [
  { field: 'delivered', label: 'Delivered', key: 'success' },
  { field: 'opened', label: 'Opened', key: 'info' },
  { field: 'clicked', label: 'Clicked', key: 'primary' },
  { field: 'bounced', label: 'Bounced', key: 'error' },
  { field: 'complained', label: 'Complained', key: 'warning' },
  { field: 'unsubscribed', label: 'Unsubscribed', key: 'textSecondary' },
];

// Divide-by-zero-guarded percentage: "0%" style output, "-" when there's nothing to divide by.
const ratePct = (numerator: number, denominator: number): string => {
  if (denominator <= 0) return '-';
  return `${Math.round((numerator / denominator) * 100)}%`;
};

const RECIPIENT_PAGE_SIZE = 50;

const Campaigns: React.FC = () => {
  const { theme } = useTheme();
  const { currentPerson } = useIdentity();

  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<{ configured: boolean; reason: string | null } | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showEngagement, setShowEngagement] = useState(false);
  // Archived campaigns are hidden by default; this toggle asks the backend for them too.
  const [showArchived, setShowArchived] = useState(false);

  // load() drives the visible spinner (manual refresh / first paint). The silent
  // flag is used by the background poll so a sending campaign's progress updates
  // on its own without flashing the "Loading…" state on every tick.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await listCampaigns({ include_archived: showArchived });
      setCampaigns(res.campaigns);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    getCampaignConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh: a reviewer keeps this page open while a campaign drips out,
  // so poll quietly every 12s. Silent (no spinner) so it never disrupts the view.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, 12000);
    return () => window.clearInterval(id);
  }, [load]);

  // ---- styles ----------------------------------------------------------------
  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '14px',
  };
  const btn = (bg: string): React.CSSProperties => ({
    backgroundColor: bg,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '7px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  });
  const ghostBtn: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: '7px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  };
  const inputStyle: React.CSSProperties = {
    backgroundColor: theme.background,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: '6px',
    padding: '6px 8px',
    fontSize: '13px',
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ color: theme.text, margin: 0 }}>Email Campaigns</h1>
      <p style={{ color: theme.textSecondary, margin: '6px 0 0', fontSize: '14px', maxWidth: '760px' }}>
        Bulk personalized sends. Stage a campaign, review it, then approve to unlock sending.
        Approved campaigns drip out one recipient at a time so nothing floods an inbox provider.
      </p>

      {config && !config.configured && (
        <div
          style={{
            ...cardStyle,
            borderColor: theme.warning,
            marginTop: '16px',
            color: theme.text,
            fontSize: '13px',
          }}
        >
          <strong style={{ color: theme.warning }}>Sending is not configured.</strong> {config.reason} You
          can still stage, review, and approve campaigns; sending stays disabled until this is resolved.
        </div>
      )}

      {/* Controls */}
      <div
        style={{
          ...cardStyle,
          marginTop: '16px',
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ color: theme.text, fontSize: '14px', fontWeight: 600 }}>
          {campaigns.length} campaign{campaigns.length === 1 ? '' : 's'}
        </span>
        <button onClick={() => setShowNewForm((v) => !v)} style={btn(theme.primary)}>
          {showNewForm ? 'Close' : 'New campaign'}
        </button>
        <button onClick={() => setShowEngagement((v) => !v)} style={ghostBtn}>
          {showEngagement ? 'Hide engagement report' : 'Engagement report'}
        </button>
        <button onClick={() => setShowArchived((v) => !v)} style={ghostBtn}>
          {showArchived ? 'Hide archived' : 'Show archived'}
        </button>
        <button onClick={() => load()} disabled={loading} style={{ ...ghostBtn, marginLeft: 'auto' }}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {banner && <div style={{ ...cardStyle, color: theme.text, fontSize: '13px' }}>{banner}</div>}

      {showEngagement && <EngagementReportPanel theme={theme} cardStyle={cardStyle} ghostBtn={ghostBtn} />}

      {showNewForm && (
        <NewCampaignForm
          theme={theme}
          cardStyle={cardStyle}
          btn={btn}
          inputStyle={inputStyle}
          onCreated={() => {
            setShowNewForm(false);
            setBanner('Campaign staged as a draft.');
            load();
          }}
          onError={(msg) => setBanner(msg)}
        />
      )}

      {/* Campaigns */}
      {loading && campaigns.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>Loading…</div>
      ) : campaigns.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>
          No campaigns yet. Stage one with "New campaign".
        </div>
      ) : (
        campaigns.map((c) => (
          <CampaignCard
            key={c.id}
            campaign={c}
            theme={theme}
            cardStyle={cardStyle}
            btn={btn}
            ghostBtn={ghostBtn}
            canSend={!!config?.configured}
            approverName={personLabel(currentPerson)}
            expanded={expandedId === c.id}
            onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
            onChanged={load}
            setBanner={setBanner}
          />
        ))
      )}
    </div>
  );
};

// ---- cross-campaign engagement report -----------------------------------------

const MIN_CAMPAIGNS_OPTIONS = [1, 2, 3, 5];

const EngagementReportPanel: React.FC<{
  theme: any;
  cardStyle: React.CSSProperties;
  ghostBtn: React.CSSProperties;
}> = ({ theme, cardStyle, ghostBtn }) => {
  const [minCampaigns, setMinCampaigns] = useState(1);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<{
    count: number;
    contacts: ContactEngagement[];
    totals: { unique_contacts: number; campaigns_counted: number };
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEngagementReport({ min_campaigns: minCampaigns });
      setReport(res);
    } finally {
      setLoading(false);
    }
  }, [minCampaigns]);

  useEffect(() => {
    load();
  }, [load]);

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '6px 10px',
    color: theme.textSecondary,
    fontWeight: 600,
    fontSize: '11px',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${theme.border}`,
  };
  const tdStyle: React.CSSProperties = { padding: '6px 10px', fontSize: '13px', whiteSpace: 'nowrap' };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <h3 style={{ color: theme.text, margin: 0, fontSize: '15px', flex: '1 1 auto' }}>
          Cross-campaign engagement
        </h3>
        <label style={{ color: theme.textSecondary, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Seen on at least
          <select
            value={minCampaigns}
            onChange={(e) => setMinCampaigns(parseInt(e.target.value, 10))}
            style={{
              backgroundColor: theme.background,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              padding: '4px 6px',
              fontSize: '12px',
            }}
          >
            {MIN_CAMPAIGNS_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} campaign{n === 1 ? '' : 's'}
              </option>
            ))}
          </select>
        </label>
        <button onClick={() => load()} disabled={loading} style={ghostBtn}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <p style={{ color: theme.textSecondary, fontSize: '12px', margin: '0 0 10px' }}>
        Who consistently opens Spiritual Data mail, across every campaign they were actually sent (not just
        one send). Sorted by open rate. Note: opens can be inflated by Apple Mail Privacy Protection
        pre-fetching the tracking pixel whether or not a person actually read the email, so click rate is
        the more reliable engagement signal.
      </p>

      {report && (
        <div style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '10px' }}>
          {report.totals.unique_contacts} unique contact{report.totals.unique_contacts === 1 ? '' : 's'} across{' '}
          {report.totals.campaigns_counted} campaign{report.totals.campaigns_counted === 1 ? '' : 's'} sent
          {report.count !== report.totals.unique_contacts ? ` · ${report.count} shown at this filter` : ''}
        </div>
      )}

      {loading && !report ? (
        <div style={{ color: theme.textSecondary, fontSize: '13px', padding: '10px 0' }}>Loading…</div>
      ) : !report || report.contacts.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: '13px', padding: '10px 0' }}>
          No sent recipients at this filter yet.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: `1px solid ${theme.border}`, borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Contact</th>
                <th style={thStyle}>Campaigns sent</th>
                <th style={thStyle}>Opened</th>
                <th style={thStyle}>Open rate</th>
                <th style={thStyle}>Clicked</th>
                <th style={thStyle}>Click rate</th>
                <th style={thStyle}>Last opened</th>
              </tr>
            </thead>
            <tbody>
              {report.contacts.map((c) => (
                <tr key={c.email} style={{ borderTop: `1px solid ${theme.border}` }}>
                  <td style={{ ...tdStyle, whiteSpace: 'normal' }}>
                    <div style={{ color: theme.text }}>{c.full_name || c.first_name || c.email}</div>
                    <div style={{ color: theme.textSecondary, fontSize: '11px' }}>{c.email}</div>
                  </td>
                  <td style={{ ...tdStyle, color: theme.text }}>{c.campaigns_sent}</td>
                  <td style={{ ...tdStyle, color: theme.text }}>{c.opened}</td>
                  <td style={{ ...tdStyle, color: c.open_rate >= 50 ? theme.success : theme.text, fontWeight: 600 }}>
                    {c.open_rate}%
                  </td>
                  <td style={{ ...tdStyle, color: theme.text }}>{c.clicked}</td>
                  <td style={{ ...tdStyle, color: c.click_rate > 0 ? theme.primary : theme.text, fontWeight: 600 }}>
                    {c.click_rate}%
                  </td>
                  <td style={{ ...tdStyle, color: theme.textSecondary }}>{fmtTime(c.last_opened_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---- progress bar -----------------------------------------------------------

const ProgressBar: React.FC<{ campaign: EmailCampaign; theme: any }> = ({ campaign, theme }) => {
  const { total, sent, failed, skipped, pending } = campaign.progress;
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
  return (
    <div>
      <div
        style={{
          height: '8px',
          borderRadius: '999px',
          backgroundColor: theme.background,
          border: `1px solid ${theme.border}`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            backgroundColor: theme.success,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '6px' }}>
        {sent} / {total} sent ({pct}%) · {failed} failed · {pending} pending · {skipped} skipped
      </div>
    </div>
  );
};

// ---- engagement summary -------------------------------------------------------

const EngagementSummary: React.FC<{ campaign: EmailCampaign; theme: any }> = ({ campaign, theme }) => {
  const { sent, delivered, opened, clicked, bounced, complained, unsubscribed } = campaign.progress;

  const chipStyle = (color: string): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    border: `1px solid ${color}`,
    borderRadius: '8px',
    padding: '6px 10px',
    minWidth: '86px',
  });

  const allZero = delivered === 0 && opened === 0 && clicked === 0 && bounced === 0 && complained === 0 && unsubscribed === 0;

  const openRateDenom = delivered > 0 ? delivered : sent;
  const clickRateDenom = delivered > 0 ? delivered : sent;
  const openRate = ratePct(opened, openRateDenom);
  const clickRate = ratePct(clicked, clickRateDenom);
  const bounceRate = ratePct(bounced, sent);
  const unsubRate = ratePct(unsubscribed, sent);

  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>
        Engagement
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {ENGAGEMENT_STAT_META.map((m) => {
          const color = (theme as any)[m.key] || theme.textSecondary;
          const value = campaign.progress[m.field];
          return (
            <div key={m.field} style={chipStyle(color)}>
              <span style={{ color, fontSize: '16px', fontWeight: 700 }}>{value}</span>
              <span style={{ color: theme.textSecondary, fontSize: '11px' }}>{m.label}</span>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '10px', fontSize: '12px', color: theme.textSecondary }}>
        <span>Open rate <strong style={{ color: theme.text }}>{openRate}</strong></span>
        <span>Click rate <strong style={{ color: theme.text }}>{clickRate}</strong></span>
        <span>Bounce rate <strong style={{ color: theme.text }}>{bounceRate}</strong></span>
        <span>Unsubscribe rate <strong style={{ color: theme.text }}>{unsubRate}</strong></span>
      </div>
      {allZero && (
        <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '8px' }}>
          No engagement events yet. Connect the Resend webhook to populate these.
        </div>
      )}
    </div>
  );
};

// ---- one campaign card -------------------------------------------------------

interface CampaignCardProps {
  campaign: EmailCampaign;
  theme: any;
  cardStyle: React.CSSProperties;
  btn: (bg: string) => React.CSSProperties;
  ghostBtn: React.CSSProperties;
  canSend: boolean;
  approverName: string;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
  setBanner: (s: string | null) => void;
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  theme,
  cardStyle,
  btn,
  ghostBtn,
  canSend,
  approverName,
  expanded,
  onToggle,
  onChanged,
  setBanner,
}) => {
  const [busy, setBusy] = useState(false);
  const meta = STATUS_META[campaign.status] || UNKNOWN_STATUS_META;
  const labelColor = (theme as any)[meta.key] || theme.textSecondary;

  const run = async (label: string, action: () => Promise<any>) => {
    setBusy(true);
    setBanner(null);
    try {
      await action();
      onChanged();
    } catch (err: any) {
      setBanner(errMsg(err, `Could not ${label}.`));
    } finally {
      setBusy(false);
    }
  };

  const approve = () => run('approve the campaign', () => approveCampaign(campaign.id, approverName));
  const recordGo = () =>
    run('record the go', () => recordPreviewGo(campaign.id, `Go recorded on the Campaigns page by ${approverName}`));
  const send = () => run('send the campaign', () => sendCampaign(campaign.id));
  const pause = () => run('pause the campaign', () => pauseCampaign(campaign.id));
  const cancel = () => run('cancel the campaign', () => cancelCampaign(campaign.id));
  const archive = () => run('archive the campaign', () => archiveCampaign(campaign.id));
  const unarchive = () => run('bring the campaign back', () => unarchiveCampaign(campaign.id));

  // Deleting is destructive and unrecoverable, so it confirms by name. The backend allows it only
  // for a campaign that never sent, and delete_blocked_reason carries why when it doesn't.
  const remove = () => {
    const ok = window.confirm(
      `Delete "${campaign.name}"?\n\nThis permanently removes the campaign and its ` +
        `${campaign.progress.total} staged recipient${campaign.progress.total === 1 ? '' : 's'}. ` +
        'It never sent an email, so nothing outward is undone. This cannot be reversed.'
    );
    if (ok) run('delete the campaign', () => deleteCampaign(campaign.id));
  };

  return (
    <div style={cardStyle}>
      {/* Header: name + status chip, click to expand */}
      <div
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '10px', flexWrap: 'wrap', cursor: 'pointer' }}
      >
        <h3 style={{ color: theme.text, margin: 0, fontSize: '16px', flex: '1 1 auto' }}>{campaign.name}</h3>
        <span
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: labelColor,
            border: `1px solid ${labelColor}`,
            borderRadius: '999px',
            padding: '2px 10px',
            whiteSpace: 'nowrap',
          }}
        >
          {meta.label}
        </span>
        {campaign.archived_at && (
          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: theme.textSecondary,
              border: `1px dashed ${theme.border}`,
              borderRadius: '999px',
              padding: '2px 10px',
              whiteSpace: 'nowrap',
            }}
          >
            Archived
          </span>
        )}
        <span style={{ color: theme.textSecondary, fontSize: '12px' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      <div style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '10px' }}>
        {personLabel(campaign.person)}
        {campaign.created_at ? ` · staged ${fmtTime(campaign.created_at)}` : ''}
        {campaign.started_at ? ` · started ${fmtTime(campaign.started_at)}` : ''}
        {campaign.completed_at ? ` · completed ${fmtTime(campaign.completed_at)}` : ''}
      </div>

      <ProgressBar campaign={campaign} theme={theme} />

      {campaign.error && (
        <div style={{ color: theme.error, fontSize: '12px', marginTop: '10px' }}>{campaign.error}</div>
      )}

      {expanded && (
        <div
          style={{
            marginTop: '14px',
            paddingTop: '14px',
            borderTop: `1px solid ${theme.border}`,
          }}
        >
          <div style={{ color: theme.text, fontSize: '13px', marginBottom: '10px' }}>
            <div style={{ marginBottom: '4px' }}>
              <strong>Subject:</strong> {campaign.subject}
            </div>
            <div style={{ color: theme.textSecondary, marginBottom: '10px' }}>
              From {campaign.from_addr || '(default)'}
              {campaign.reply_to ? ` · reply to ${campaign.reply_to}` : ''} · interval {campaign.interval_seconds}s
              {campaign.source_csv ? ` · from ${campaign.source_csv}` : ''}
              {campaign.approved_by ? ` · approved by ${campaign.approved_by}` : ''}
            </div>
            <div style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '4px' }}>
              Email content (template; each recipient gets their name filled in, view a recipient below to see theirs):
            </div>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                backgroundColor: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                padding: '12px',
                color: theme.text,
                fontSize: '13px',
                fontFamily: 'inherit',
                maxHeight: '340px',
                overflowY: 'auto',
                margin: 0,
              }}
            >
              {campaign.template_body}
            </pre>
          </div>
          <EngagementSummary campaign={campaign} theme={theme} />

          <RecipientsPanel campaignId={campaign.id} theme={theme} ghostBtn={ghostBtn} />
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
        {campaign.status === 'awaiting_joshua_preview' && (
          <button onClick={recordGo} disabled={busy} style={btn(theme.success)}>
            Record go
          </button>
        )}
        {campaign.status === 'draft' && (
          <button onClick={approve} disabled={busy} style={btn(theme.success)}>
            Approve
          </button>
        )}
        {(campaign.status === 'approved' || campaign.status === 'paused') && (
          <button
            onClick={send}
            disabled={busy || !canSend}
            title={canSend ? '' : 'Sending not configured yet'}
            style={{ ...btn(theme.success), opacity: canSend ? 1 : 0.5, cursor: canSend ? 'pointer' : 'not-allowed' }}
          >
            {campaign.status === 'paused' ? 'Resume' : 'Send'}
          </button>
        )}
        {campaign.status === 'sending' && (
          <button onClick={pause} disabled={busy} style={btn(theme.warning)}>
            Pause
          </button>
        )}
        {campaign.status !== 'sent' && campaign.status !== 'cancelled' && (
          <button onClick={cancel} disabled={busy} style={ghostBtn}>
            Cancel
          </button>
        )}
        {/* Off-the-list actions. Delete is only offered when the backend says it is safe (a
            campaign that never mailed anyone); everything else archives, which keeps the row and
            its opens/clicks and just hides it. */}
        {campaign.archived_at ? (
          <button onClick={unarchive} disabled={busy} style={ghostBtn}>
            Unarchive
          </button>
        ) : (
          campaign.status !== 'sending' && (
            <button onClick={archive} disabled={busy} style={ghostBtn}>
              Archive
            </button>
          )
        )}
        {campaign.delete_blocked_reason ? (
          <button
            disabled
            title={campaign.delete_blocked_reason}
            style={{ ...ghostBtn, opacity: 0.4, cursor: 'not-allowed' }}
          >
            Delete
          </button>
        ) : (
          <button
            onClick={remove}
            disabled={busy}
            title="Permanently delete this campaign. It never sent an email."
            style={{ ...ghostBtn, color: theme.error, borderColor: theme.error }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

// ---- recipients panel (filterable, paginated) --------------------------------

const RECIPIENT_FILTERS: { label: string; value: CampaignRecipientStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Sent', value: 'sent' },
  { label: 'Failed', value: 'failed' },
  { label: 'Pending', value: 'pending' },
  { label: 'Skipped', value: 'skipped' },
];

const RecipientsPanel: React.FC<{
  campaignId: number;
  theme: any;
  ghostBtn: React.CSSProperties;
}> = ({ campaignId, theme, ghostBtn }) => {
  const [filter, setFilter] = useState<CampaignRecipientStatus | 'all'>('all');
  const [offset, setOffset] = useState(0);
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // Which recipient's full personalized message is expanded (click a row to view it).
  const [openRid, setOpenRid] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCampaignRecipients(campaignId, {
        status: filter === 'all' ? undefined : filter,
        limit: RECIPIENT_PAGE_SIZE,
        offset,
      });
      setRecipients(res.recipients);
      setTotal(res.count);
    } finally {
      setLoading(false);
    }
  }, [campaignId, filter, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to the first page whenever the filter changes.
  useEffect(() => {
    setOffset(0);
  }, [filter]);

  const filterBtnStyle = (active: boolean): React.CSSProperties => ({
    ...ghostBtn,
    padding: '4px 10px',
    backgroundColor: active ? theme.primary : 'transparent',
    color: active ? '#FFFFFF' : theme.textSecondary,
    borderColor: active ? theme.primary : theme.border,
  });

  const page = Math.floor(offset / RECIPIENT_PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / RECIPIENT_PAGE_SIZE));

  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px', alignItems: 'center' }}>
        {RECIPIENT_FILTERS.map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)} style={filterBtnStyle(filter === f.value)}>
            {f.label}
          </button>
        ))}
        <span style={{ color: theme.textSecondary, fontSize: '12px', marginLeft: 'auto' }}>
          {total} recipient{total === 1 ? '' : 's'}
        </span>
      </div>

      {loading && recipients.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: '13px', padding: '10px 0' }}>Loading…</div>
      ) : recipients.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: '13px', padding: '10px 0' }}>No recipients here.</div>
      ) : (
        <div
          style={{
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          {recipients.map((r, i) => {
            const rMeta = RECIPIENT_STATUS_META[r.status] || UNKNOWN_STATUS_META;
            const rColor = (theme as any)[rMeta.key] || theme.textSecondary;
            const open = openRid === r.id;
            return (
              <div
                key={r.id}
                style={{
                  borderTop: i === 0 ? 'none' : `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                }}
              >
                <div
                  onClick={() => setOpenRid(open ? null : r.id)}
                  title="Click to view this recipient's message"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 10px',
                    flexWrap: 'wrap',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ color: theme.textSecondary, fontSize: '11px', width: '10px' }}>{open ? '▾' : '▸'}</span>
                  <span style={{ color: theme.text, fontSize: '13px', flex: '1 1 200px' }}>{r.email}</span>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: rColor,
                      border: `1px solid ${rColor}`,
                      borderRadius: '999px',
                      padding: '1px 8px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {rMeta.label}
                  </span>
                  <span style={{ color: theme.textSecondary, fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {fmtTime(r.sent_at)}
                  </span>
                  {RECIPIENT_ENGAGEMENT_META.filter((m) => !!r[m.field]).map((m) => {
                    const color = (theme as any)[m.key] || theme.textSecondary;
                    return (
                      <span
                        key={m.field}
                        title={fmtTime(r[m.field])}
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color,
                          border: `1px solid ${color}`,
                          borderRadius: '999px',
                          padding: '1px 7px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.label}
                      </span>
                    );
                  })}
                  {r.status === 'failed' && r.error && (
                    <span style={{ color: theme.error, fontSize: '12px', flex: '1 1 100%' }}>{r.error}</span>
                  )}
                </div>
                {open && (
                  <div style={{ padding: '0 10px 12px 30px' }}>
                    <div style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '4px' }}>
                      Subject: {r.first_name ? `(personalized for ${r.first_name})` : ''} · the exact message this recipient gets:
                    </div>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        backgroundColor: theme.background,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '8px',
                        padding: '12px',
                        color: theme.text,
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        margin: 0,
                        maxHeight: '340px',
                        overflowY: 'auto',
                      }}
                    >
                      {r.body}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pageCount > 1 && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
          <button
            onClick={() => setOffset(Math.max(0, offset - RECIPIENT_PAGE_SIZE))}
            disabled={offset === 0}
            style={{ ...ghostBtn, padding: '4px 10px', opacity: offset === 0 ? 0.5 : 1 }}
          >
            Prev
          </button>
          <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
            Page {page} of {pageCount}
          </span>
          <button
            onClick={() => setOffset(offset + RECIPIENT_PAGE_SIZE)}
            disabled={offset + RECIPIENT_PAGE_SIZE >= total}
            style={{ ...ghostBtn, padding: '4px 10px', opacity: offset + RECIPIENT_PAGE_SIZE >= total ? 0.5 : 1 }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// ---- new campaign form --------------------------------------------------------

const NewCampaignForm: React.FC<{
  theme: any;
  cardStyle: React.CSSProperties;
  btn: (bg: string) => React.CSSProperties;
  inputStyle: React.CSSProperties;
  onCreated: () => void;
  onError: (msg: string) => void;
}> = ({ theme, cardStyle, btn, inputStyle, onCreated, onError }) => {
  const [name, setName] = useState('');
  const [person, setPerson] = useState('zee');
  const [subject, setSubject] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [sourceCsv, setSourceCsv] = useState('');
  const [limit, setLimit] = useState('');
  const [intervalSeconds, setIntervalSeconds] = useState('120');
  const [busy, setBusy] = useState(false);

  const labelStyle: React.CSSProperties = { color: theme.textSecondary, fontSize: '12px', display: 'block', marginBottom: '4px' };
  const fieldStyle: React.CSSProperties = { marginBottom: '12px' };

  const canSubmit = name.trim() && subject.trim() && templateBody.trim() && sourceCsv.trim();

  const submit = async () => {
    setBusy(true);
    onError('');
    try {
      await createCampaign({
        name: name.trim(),
        person: person.trim().toLowerCase() || 'zee',
        subject: subject.trim(),
        template_body: templateBody,
        source_csv: sourceCsv.trim(),
        limit: limit.trim() ? parseInt(limit, 10) : undefined,
        interval_seconds: intervalSeconds.trim() ? parseInt(intervalSeconds, 10) : undefined,
      });
      setName('');
      setSubject('');
      setTemplateBody('');
      setSourceCsv('');
      setLimit('');
      setIntervalSeconds('120');
      onCreated();
    } catch (err: any) {
      onError(errMsg(err, 'Could not stage the campaign.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ color: theme.text, margin: '0 0 12px', fontSize: '15px' }}>New campaign</h3>

      <div style={fieldStyle}>
        <label style={labelStyle}>Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder="e.g. Revenue Challenge invite" />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Person (whose voice this sends as)</label>
        <input value={person} onChange={(e) => setPerson(e.target.value)} style={{ ...inputStyle, width: '200px' }} />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Subject</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Template body (supports &#123;&#123;first_name&#125;&#125; and &#123;&#123;sender_signature&#125;&#125;)</label>
        <textarea
          value={templateBody}
          onChange={(e) => setTemplateBody(e.target.value)}
          rows={8}
          style={{ ...inputStyle, width: '100%', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ ...fieldStyle, flex: '1 1 240px' }}>
          <label style={labelStyle}>Source CSV path (recipients: email, first_name, full_name)</label>
          <input value={sourceCsv} onChange={(e) => setSourceCsv(e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder="/path/to/recipients.csv" />
        </div>
        <div style={{ ...fieldStyle, width: '140px' }}>
          <label style={labelStyle}>Limit (optional)</label>
          <input value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^0-9]/g, ''))} style={{ ...inputStyle, width: '100%' }} placeholder="all" />
        </div>
        <div style={{ ...fieldStyle, width: '140px' }}>
          <label style={labelStyle}>Interval (seconds)</label>
          <input
            value={intervalSeconds}
            onChange={(e) => setIntervalSeconds(e.target.value.replace(/[^0-9]/g, ''))}
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>
      </div>

      <p style={{ color: theme.textSecondary, fontSize: '12px', margin: '0 0 12px' }}>
        This stages a draft only. Nothing sends until you approve it, then click Send.
      </p>

      <button onClick={submit} disabled={busy || !canSubmit} style={{ ...btn(theme.primary), opacity: canSubmit ? 1 : 0.5 }}>
        {busy ? 'Staging…' : 'Stage draft'}
      </button>
    </div>
  );
};

export default Campaigns;
